import { z } from 'zod'
import type { Hex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import type { KaiascanClient } from './client'
import { kaiascanInternalTransactionSchema } from './schema'

type GetInternalTransactionsByAddressParameters = {
  client: KaiascanClient
  address: Hex
  startblock?: number
  endblock?: number
  page?: number
  offset?: number
  sort?: 'asc' | 'desc'
  headers?: Record<string, string>
}

const resultSchema = z.array(kaiascanInternalTransactionSchema)

/**
 * Kaiascan から address の internal transaction 一覧を取得する。
 *
 * @param parameters - client、address、block range、pagination、headers。
 * @returns internal transaction の配列。
 */
export const getInternalTransactionsByAddress = async ({
  client,
  address,
  startblock = 0,
  endblock,
  page = 1,
  offset = 500,
  sort = 'asc',
  headers,
}: GetInternalTransactionsByAddressParameters): Promise<
  (InternalTransactionWithIndex & { isRoot: boolean })[]
> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'txlistinternal',
        address,
        startblock: startblock.toString(),
        // Kaiascan の txlistinternal は endblock 指定時に internal index error を返すため、取得後に絞り込む。
        page: page.toString(),
        offset: offset.toString(),
        sort,
      },
      resultSchema,
      headers,
    })
    return (
      endblock === undefined
        ? result
        : result.filter((transaction) => transaction.blockNumber <= endblock)
    ).map((transaction) => ({
      // traceId は block number、5 桁の transaction index、5 桁の call index。
      isRoot: transaction.traceId?.endsWith('00000') ?? false,
      blockNumber: transaction.blockNumber,
      contractAddress: transaction.contractAddress,
      from: transaction.from,
      gas: transaction.gas,
      isError: transaction.isError,
      timestamp: transaction.timestamp,
      to: transaction.to,
      txHash: transaction.hash,
      value: transaction.value,
    }))
  } catch (error) {
    if (
      error instanceof Error &&
      (error.message.includes('No transactions found') ||
        error.message === 'No internal transactions found')
    ) {
      return []
    }
    throw error
  }
}
